import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { API_ENDPOINT } from "../../utils/constants";
import { getBillingOptions, durationLabel } from "../../utils/pricingPlanUtils";
import { getHeader } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";

const formatMoney = (paise) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(paise / 100);

const readJson = async (response) => response.json().catch(() => ({}));

const RazorpayPlanCheckout = () => {
  const { planId, cycleKey } = useParams();
  const navigate = useNavigate();
  const [selection, setSelection] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [plansResponse, subscriptionResponse] = await Promise.all([
          fetch(`${API_ENDPOINT}/root/pricing-plans`, {
            method: "GET",
            ...getHeader(),
          }),
          fetch(`${API_ENDPOINT}/payment/my-razorpay-plan-subscription`, {
            method: "GET",
            ...getHeader(),
          }),
        ]);
        const plansData = await readJson(plansResponse);
        if (!plansResponse.ok) {
          throw new Error(plansData.message || "Could not load plans");
        }
        const plan = (plansData.plans || []).find(
          (item) => item._id === planId && item.active !== false
        );
        const cycle =
          plan && getBillingOptions(plan).find((item) => item.key === cycleKey);
        if (!cycle) {
          throw new Error("This billing option is no longer available.");
        }
        const subscriptionData = subscriptionResponse.ok
          ? await readJson(subscriptionResponse)
          : {};
        if (active) {
          setSelection({ plan, cycle });
          setSubscription(subscriptionData.subscription || null);
        }
      } catch (err) {
        if (active) setError(err.message || "Could not load plan");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [planId, cycleKey]);

  const submit = async () => {
    if (!selection || processing) return;
    setProcessing(true);
    setError("");
    try {
      const isFree = selection.plan.isFree === true;
      const response = await fetch(
        `${API_ENDPOINT}/payment/${
          isFree ? "activate-free-plan" : "create-razorpay-plan-subscription"
        }`,
        {
          method: "POST",
          ...getHeader(),
          body: JSON.stringify({ planId, cycleKey }),
        }
      );
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Could not start checkout");
      }
      if (isFree) {
        setResult("Your free plan is active.");
        return;
      }
      if (
        data.pricing.basePaise !== Math.round(Number(selection.cycle.price) * 100) ||
        data.months !== selection.cycle.months
      ) {
        throw new Error(
          "This plan changed while you were reviewing it. Return to pricing plans and review the new price."
        );
      }
      setSubscription({
        id: data.subscriptionId,
        status: "created",
        name: data.name,
        label: data.label,
        planId,
        cycleKey,
      });
      if (!window.Razorpay) {
        throw new Error("Razorpay Checkout did not load. Please refresh and try again.");
      }

      const checkout = new window.Razorpay({
        key: data.keyId,
        subscription_id: data.subscriptionId,
        name: "Smart Growth Solutions",
        description: `${data.name} — renews every ${durationLabel(data.months)}`,
        handler: async (payment) => {
          try {
            const verification = await fetch(
              `${API_ENDPOINT}/payment/verify-razorpay-plan-subscription`,
              {
                method: "POST",
                ...getHeader(),
                body: JSON.stringify({
                  subscriptionId: data.subscriptionId,
                  paymentId: payment.razorpay_payment_id,
                  signature: payment.razorpay_signature,
                }),
              }
            );
            const verified = await readJson(verification);
            if (!verification.ok) {
              if (verification.status === 502) {
                setResult(
                  "Payment submitted. Your plan will activate after confirmation."
                );
              } else {
                setError(verified.message || "Payment verification failed");
              }
              return;
            }
            setResult("Your recurring plan is active.");
            setSubscription({
              id: data.subscriptionId,
              status: "active",
              name: data.name,
              label: data.label,
            });
          } catch (err) {
            setResult("Payment submitted. Your plan will activate after confirmation.");
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => setProcessing(false),
        },
        theme: { color: "#128C7E" },
      });
      checkout.on("payment.failed", (failure) => {
        setError(
          failure?.error?.description || "Payment failed. Please try again."
        );
        setProcessing(false);
      });
      checkout.open();
    } catch (err) {
      setError(err.message || "Could not start checkout");
      setProcessing(false);
    } finally {
      if (selection.plan.isFree === true) setProcessing(false);
    }
  };

  const cancelSubscription = async () => {
    const pendingCheckout = subscription?.status === "created";
    if (
      !window.confirm(
        pendingCheckout
          ? "Cancel this unfinished checkout?"
          : "Stop this subscription from renewing?"
      )
    ) return;
    setProcessing(true);
    setError("");
    try {
      const response = await fetch(
        `${API_ENDPOINT}/payment/cancel-razorpay-plan-subscription`,
        { method: "POST", ...getHeader() }
      );
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Could not stop renewal");
      }
      setSubscription((previous) => ({
        ...previous,
        status: data.status,
        scheduledCancel: data.scheduledCancel,
      }));
      setResult(
        pendingCheckout
          ? "Unfinished checkout cancelled. You can start again."
          : data.scheduledCancel
          ? "Renewal will stop at the end of the current billing period."
          : "Subscription cancelled."
      );
    } catch (err) {
      setError(err.message || "Could not stop renewal");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  const isFree = selection?.plan.isFree === true;
  const basePaise = Math.round(Number(selection?.cycle.price || 0) * 100);
  const gstPaise = Math.round(basePaise * 0.18);
  const hasPendingCheckout = subscription?.status === "created";
  const pendingCheckoutMatchesSelection =
    hasPendingCheckout &&
    subscription.planId === planId &&
    subscription.cycleKey === cycleKey;
  const hasRecurringSubscription =
    subscription &&
    !["created", "cancelled", "completed", "expired"].includes(subscription.status);

  return (
    <Container maxWidth="sm" sx={{ mt: 3, pb: 5 }}>
      <BackBtn />
      <Card sx={{ p: { xs: 3, sm: 4 }, mt: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={700}>
            {isFree ? "Activate free plan" : "Recurring plan checkout"}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {result && <Alert severity="success">{result}</Alert>}
          {selection && (
            <>
              <Typography variant="h6">{selection.plan.name}</Typography>
              <Typography color="text.secondary">
                {selection.cycle.label || durationLabel(selection.cycle.months)}
              </Typography>
              <Divider />
              <Typography>
                Plan price: {isFree ? "Free" : formatMoney(basePaise)}
              </Typography>
              {!isFree && (
                <>
                  <Typography>GST (18%): {formatMoney(gstPaise)}</Typography>
                  <Typography fontWeight={700}>
                    Charged every {durationLabel(selection.cycle.months)}:{" "}
                    {formatMoney(basePaise + gstPaise)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This payment renews automatically for up to ten years unless
                    you stop renewal.
                  </Typography>
                </>
              )}
              {(!result || subscription?.status === "cancelled") &&
                !hasRecurringSubscription &&
                (!hasPendingCheckout || pendingCheckoutMatchesSelection) && (
                <Button variant="contained" disabled={processing} onClick={submit}>
                  {processing
                    ? "Processing..."
                    : isFree
                      ? "Activate plan"
                      : hasPendingCheckout
                        ? "Continue checkout"
                        : "Start recurring subscription"}
                </Button>
              )}
              {hasPendingCheckout && !isFree && (
                <Alert severity="info">
                  Checkout for {subscription.name} ({subscription.label}) was not
                  completed. {pendingCheckoutMatchesSelection
                    ? "Continue it, or cancel it to choose another plan."
                    : "Cancel it before choosing this plan."}
                </Alert>
              )}
              {hasRecurringSubscription && !isFree && (
                <Alert severity="info">
                  Your current subscription is {subscription.status}.
                  {subscription.scheduledCancel &&
                    " Renewal is scheduled to stop."}
                </Alert>
              )}
              {(hasRecurringSubscription || hasPendingCheckout) &&
                !isFree &&
                !subscription.scheduledCancel && (
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={processing}
                    onClick={cancelSubscription}
                  >
                    {hasPendingCheckout ? "Cancel pending checkout" : "Stop renewal"}
                  </Button>
                )}
              {result && (
                <Button variant="contained" onClick={() => navigate("/home")}>
                  Go to dashboard
                </Button>
              )}
            </>
          )}
        </Stack>
      </Card>
    </Container>
  );
};

export default RazorpayPlanCheckout;
